/**
 * QuickTimeGrid Component
 *
 * 4-column grid of common scheduling times for quick selection
 */

import { useMemo, useCallback, useRef, useEffect, KeyboardEvent } from 'react';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { QuickTimeGridProps, QUICK_SELECT_TIMES } from './types';

export const QuickTimeGrid = ({
  selectedTime,
  onTimeSelect,
  selectedDate,
  timezone,
  allowPast = false,
  disabled = false,
}: QuickTimeGridProps) => {
  const { t } = useTranslation();
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  // Check if a time is in the past
  const isTimePast = useCallback(
    (time: string): boolean => {
      if (allowPast || !selectedDate) return false;

      const now = new Date();
      // selectedDate is a browser-local DayPicker cell. Compare its calendar
      // fields with "today" in the account timezone, not with browser midnight.
      const selectedDay = format(selectedDate, 'yyyy-MM-dd');
      const currentDay = timezone
        ? formatInTimeZone(now, timezone, 'yyyy-MM-dd')
        : format(now, 'yyyy-MM-dd');
      const currentTime = timezone
        ? formatInTimeZone(now, timezone, 'HH:mm')
        : format(now, 'HH:mm');
      return selectedDay === currentDay && time < currentTime;
    },
    [selectedDate, timezone, allowPast]
  );

  // Convert 24-hour time to 12-hour format with AM/PM
  const formatTime12Hour = useCallback((time: string): string => {
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  }, []);

  // Get times, filtering out past ones
  const times = useMemo(
    () =>
      QUICK_SELECT_TIMES
        .filter((time) => !isTimePast(time))
        .map((time) => ({
          value: time,
          display: formatTime12Hour(time),
          isSelected: time === selectedTime,
        })),
    [selectedTime, isTimePast, formatTime12Hour]
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
      let newIndex = index;

      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          newIndex = Math.min(index + 1, times.length - 1);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          newIndex = Math.max(index - 1, 0);
          break;
        case 'ArrowDown':
          e.preventDefault();
          newIndex = Math.min(index + 3, times.length - 1);
          break;
        case 'ArrowUp':
          e.preventDefault();
          newIndex = Math.max(index - 3, 0);
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (!disabled) {
            onTimeSelect(times[index].value);
          }
          return;
        default:
          return;
      }

      // Focus the new button
      const newTime = times[newIndex]?.value;
      if (newTime) {
        buttonRefs.current.get(newTime)?.focus();
      }
    },
    [times, onTimeSelect, disabled]
  );

  // Auto-focus selected time on mount
  useEffect(() => {
    if (selectedTime) {
      buttonRefs.current.get(selectedTime)?.focus();
    }
  }, []);

  if (times.length === 0) return null;

  return (
    <div>
      <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
        {t('time_quick_select')}
      </h4>

      <div className="grid grid-cols-3 gap-1.5">
        {times.map((time, index) => (
          <button
            key={time.value}
            ref={(el) => {
              if (el) {
                buttonRefs.current.set(time.value, el);
              } else {
                buttonRefs.current.delete(time.value);
              }
            }}
            type="button"
            onClick={() => !disabled && onTimeSelect(time.value)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            disabled={disabled}
            className={cn(
              'px-2 py-1.5 rounded-lg text-xs font-medium transition-all duration-150',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1',
              time.isSelected
                ? 'bg-primary-500 text-primary-contrast shadow-md'
                : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-300',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
            aria-label={`Select ${time.display}`}
            aria-pressed={time.isSelected}
            tabIndex={time.isSelected ? 0 : -1}
          >
            {time.display}
          </button>
        ))}
      </div>
    </div>
  );
};
